import { FCC } from "@/types/common";
import { Box, CircularProgress, Grid, Modal, Stack, styled, Typography } from "@mui/material";
import { useVisible, UseVisibleReturn } from "@/hooks/useVisible";
import { background, black, button, divider, orange, state } from "@/theme/colors";
import React, { Key, useRef, useState } from "react";
import { IconClose, IconFilePlus, IconSaveScanImage } from "@/assets/icons";
import { ConfirmLeaveScanModal } from "@/features/job/components/job-scan-image/ConfirmLeaveScanModal";
import { convertMbToByte } from "@/utils/file";
import { Button } from "@/components/elements/Button";
import { DropZone } from "@/components/elements/DropZone";
import { TExtendFile } from "@/types/file";
import { IconBox } from "@/components/elements/IconBox";
import { InnerBox } from "@/components/elements/InnerBox";
import { rounded } from "@/theme/variables";
import { CreateJobScanForm, TCreateJobScanFormRef } from "@/features/job/components/job-scan-image/CreateJobScanForm";
import { toast } from "@/utils/toast";
import { MESSAGE_EMPTY_DATA_FROM_SCAN } from "@/features/talent/constants/scan";
import { createJobApi, generateScanJobDescriptionApi } from "@/api/services/main/job";
import { API_SUCCESS_MESSAGE, API_UNKNOWN_ERROR_MESSAGE } from "@/constants/common";
import { INVALID_FILE_SCAN_JOB } from "@/constants/invalids";
import { TJobForm } from "@/features/job/types/JobForm";
import { convertJobFormDataReq, convertOcrScanDataToFormData } from "@/features/job/utils/convertJobFormData";
import { mapScanCheckboxesToForm } from "@/features/job/utils/mapScanCheckboxesToForm";
import { TScanCheckbox } from "@/features/job/types/ScanCheckbox";
import { showError } from "@/utils/error";
import { useNavigate } from "react-router-dom";
import { dayjs } from "@/utils/dayjs";
import { Progress } from "@/components/elements/Progress";
import { ImagePreviewWithZoom } from "@/features/talent/components/talent-scan-image/ImagePreviewWithZoom";
import { DocxPreviewWithZoom } from "@/features/talent/components/talent-scan-image/DocxPreviewWithZoom";
import { OfficeFilePreview } from "@/features/talent/components/talent-scan-image/OfficeFilePreview";
import { PdfPreviewWithZoom } from "@/features/talent/components/talent-scan-image/PdfPreviewWithZoom";
import { SCAN_ACCEPT_EXT_LABEL, SCAN_ACCEPT_MIME_TYPES, getScanPreviewType } from "@/features/talent/constants/scan-accept";

type TScanImageJobModal = {
  modal: UseVisibleReturn;
};

export const ScanImageJobModal: FCC<TScanImageJobModal> = ({ modal }) => {
  const [isResultData, setIsResultData] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [typeScan, setTypeScan] = useState<number>(1);
  const [defaultJobValue, setDefaultJobValue] = useState<TJobForm>();
  const [scannedCompanyName, setScannedCompanyName] = useState<string>();
  const [scannedPrefecture, setScannedPrefecture] = useState<string>();
  // Captured from BE OCR response: true when 分野 was fuzzy-matched to master DB.
  // Drives the diff warning in CreateJobScanForm.
  const [companySpecificIndustryMapped, setCompanySpecificIndustryMapped] = useState<boolean>(false);
  const [processingProgress, setProcessingProgress] = useState(0);

  const navigate = useNavigate();
  const controllerRef = useRef<AbortController | null>(null);
  const formRef = useRef<TCreateJobScanFormRef>(null);
  const confirmLeaveModal = useVisible();
  const typeScanOption = [
    {
      value: 1,
      label: "ファイルから取込",
    },
    // {
    //   value: 2,
    //   label: "URLから取込",
    // },
  ];

  const handleChangeFiles = async (value: TExtendFile | TExtendFile[] | null) => {
    const files = [value ?? []].flat();
    if (!files[0]) {
      setFile(null);
      return;
    }
    setFile(files[0]);
  };

  const validateForm = (): boolean => {
    if (!file) {
      toast.error(MESSAGE_EMPTY_DATA_FROM_SCAN);
      return false;
    }
    // Validate file is not empty (0 bytes)
    if (file.size === 0) {
      toast.error(INVALID_FILE_SCAN_JOB);
      return false;
    }
    return true;
  };

  const onSubmit = async () => {
    const isValid = validateForm();
    if (!isValid || !file) {
      return;
    }
    setIsSubmit(true);
    setProcessingProgress(0);
    controllerRef.current = new AbortController();
    try {
      const { data } = await generateScanJobDescriptionApi(file, (progress) => setProcessingProgress(progress), controllerRef.current.signal);
      if (!data) {
        toast.error(INVALID_FILE_SCAN_JOB);
        setIsSubmit(false);
        setProcessingProgress(0);
        return;
      }
      setIsResultData(true);
      const scanData = data as Record<string, unknown>;
      const convertedScanData = convertOcrScanDataToFormData(scanData);
      const jobSalarySummaries = (convertedScanData.jobSalarySummaries as TJobForm["jobSalarySummaries"]) ?? [];
      const checkboxFields = mapScanCheckboxesToForm(scanData.checkbox as TScanCheckbox[] | undefined);
      const dataDefault: TJobForm = {
        ...checkboxFields,
        ...(convertedScanData as TJobForm),
        salaryType: (convertedScanData as TJobForm).salaryType ?? checkboxFields.salaryType,
        gender: (convertedScanData as TJobForm).gender ?? checkboxFields.gender,
        japaneseLevelType: (convertedScanData as TJobForm).japaneseLevelType ?? checkboxFields.japaneseLevelType,
        otherApplicationRequirements: (convertedScanData as TJobForm).otherApplicationRequirements ?? checkboxFields.otherApplicationRequirements,
        jobSalarySummaries,
        startDate: convertedScanData.startDate ? dayjs(convertedScanData.startDate as unknown as string) : null,
        endDate: convertedScanData.endDate ? dayjs(convertedScanData.endDate as unknown as string) : null,
      };
      if (scanData.companyName) {
        setScannedCompanyName(scanData.companyName as string);
      }
      if (scanData.companyPrefecture) {
        setScannedPrefecture(scanData.companyPrefecture as string);
      }
      setCompanySpecificIndustryMapped(Boolean(scanData.companySpecificIndustryMapped));
      setDefaultJobValue(dataDefault);
      setProcessingProgress(0);
    } catch (e) {
      // Suppress error notification when user intentionally aborts the request
      // fetch API throws DOMException with name "AbortError"
      if (e instanceof DOMException && e.name === "AbortError") {
        return;
      }
      setIsSubmit(false);
      setProcessingProgress(0);
      showError(e);
    }
  };

  const createJob = async () => {
    if (!formRef.current) return;

    const { isValid, data } = await formRef.current.validateAndGetData();
    if (!isValid || !data) {
      return;
    }

    await handleSaveData(data);
  };

  const handleSaveData = async (data: TJobForm) => {
    try {
      const existingFiles = (data.jobDocumentFiles ?? []).filter((f) => f instanceof File);
      if (file) {
        existingFiles.push(file as TExtendFile);
      }

      data.jobDocumentFiles = existingFiles;
      data.jobWorkingTimes = data.jobWorkingTimes?.map((workerTime) => {
        const { id, ...filterIdData } = workerTime;
        return filterIdData;
      });
      data.jobSalarySummaries = data.jobSalarySummaries?.map((salarySummary) => {
        const { id, ...filterIdData } = salarySummary;
        return filterIdData;
      });
      const payload = convertJobFormDataReq(data);

      const { data: dataJob } = await createJobApi(payload);
      if (dataJob) {
        toast.success(API_SUCCESS_MESSAGE);
        return navigate(`/job/${dataJob.id}/edit`, {
          replace: true,
        });
      } else {
        toast.error(API_UNKNOWN_ERROR_MESSAGE);
      }
    } catch (e) {
      showError(e);
    }
  };

  const RenderPreview = () => {
    if (!file) return <></>;

    const previewType = getScanPreviewType(file.type);
    if (previewType === "tiff") return <ImagePreviewWithZoom src={file} type="tiff" height={530} />;
    if (previewType === "image") return <ImagePreviewWithZoom src={file} type="image" height={530} />;
    if (previewType === "pdf") return <PdfPreviewWithZoom src={file} height={530} />;
    if (previewType === "docx") return <DocxPreviewWithZoom src={file} height={530} />;
    return <OfficeFilePreview filename={file.name} height={530} />;
  };

  const onChosenOption = (newValue: Key) => {
    setTypeScan(Number(newValue));
  };

  const resetModalState = () => {
    setIsResultData(false);
    setIsSubmit(false);
    setFile(null);
    setDefaultJobValue(undefined);
    setScannedCompanyName(undefined);
    setScannedPrefecture(undefined);
    setCompanySpecificIndustryMapped(false);
    setProcessingProgress(0);
  };

  const onClose = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
    // Show confirm modal when form has data
    if (isResultData) {
      confirmLeaveModal.open();
      return;
    }
    resetModalState();
    modal.close();
  };

  const confirmClose = () => {
    confirmLeaveModal.close();
    resetModalState();
    modal.close();
  };
  return (
    <>
      <Modal open={modal.visible} hideBackdrop={false} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
        <ModalContainer isResultData={isResultData}>
          <Stack
            bgcolor={background.disable}
            minHeight="56px"
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            <Stack ml={3}>
              <Typography variant="body14Light" color={"#0B0C0D"}>
                求人情報｜求人票等からのAI取込
              </Typography>
            </Stack>
            <Stack mr={3} width={24}>
              <IconBox onClick={onClose}>
                <IconClose />
              </IconBox>
            </Stack>
          </Stack>
          {isResultData ? (
            <>
              <Grid container padding={3} spacing={2} height="100%">
                <Grid item xs={6}>
                  <InnerBox
                    showDivider={false}
                    borderColor={black[95]}
                    padding={2}
                    sx={{
                      height: "calc(85vh - 180px)",
                      maxHeight: "611px",
                      overflow: "auto",
                    }}
                  >
                    {file && RenderPreview()}
                  </InnerBox>
                </Grid>
                <Grid item xs={6}>
                  <InnerBox
                    showDivider={false}
                    borderColor={black[95]}
                    padding={2}
                    sx={{
                      height: "calc(85vh - 180px)",
                      maxHeight: "611px",
                      overflow: "auto",
                    }}
                  >
                    <Stack>
                      <Stack
                        sx={{
                          p: "8px 16px 8px 16px",
                          background: state.incomplete_secondary,
                          color: orange[80],
                          height: "34px",
                          marginBottom: "16px",
                        }}
                        borderRadius={rounded.xs}
                      >
                        <Typography component="p" variant="cap12">
                          AIによる読み取り結果は青字で示されるます。抽出ミスがないか確認して、必要に応じて修正ください。
                        </Typography>
                      </Stack>
                    </Stack>
                    <CreateJobScanForm
                      ref={formRef}
                      defaultValue={defaultJobValue}
                      scannedCompanyName={scannedCompanyName}
                      scannedPrefecture={scannedPrefecture}
                      companySpecificIndustryMapped={companySpecificIndustryMapped}
                    />
                  </InnerBox>
                </Grid>
              </Grid>
              <Box>
                <hr color={divider.high} />
                <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px" minHeight={69}>
                  <Button size="sm" variant="ghost" onClick={onClose}>
                    キャンセル
                  </Button>
                  <Button size="sm" variant="primary" onClick={createJob} startIcon={<IconFilePlus />}>
                    上記内容で編集画面に反映
                  </Button>
                </Stack>
              </Box>
            </>
          ) : (
            <>
              {isSubmit ? (
                <Box sx={{ overflowY: "hidden", maxHeight: "160px" }}>
                  <Box sx={{ px: 3, pt: 6 }}>
                    <ProcessingProgressBar value={processingProgress} />
                  </Box>
                  <Stack direction="row" alignItems="center" justifyContent="center" mt={1} spacing={1} height={60}>
                    <CircularProgress
                      sx={{
                        color: black[90],
                      }}
                      size={20}
                    />
                    <Typography variant="body14Semi" textAlign="center">
                      処理中（1分程度かかる場合がございます）
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <Box sx={{ overflowY: "auto", maxHeight: "calc(90vh - 56px - 72px)" }}>
                  <Stack sx={{ p: 4, gap: "10px" }}>
                    <Grid container columnSpacing={3} rowSpacing={2}>
                      {/* TODO: Continue to implement the next phase*/}
                      {/*<Grid item xs={12}>*/}
                      {/*  <Typography variant="cap12" color={black[30]}>*/}
                      {/*    取込方法*/}
                      {/*    <Typography component="span" variant="cap12" sx={{ color: red[700], ml: 0.5 }}>*/}
                      {/*      **/}
                      {/*    </Typography>*/}
                      {/*  </Typography>*/}
                      {/*  <RadioGroup value={typeScan} direction="row" options={typeScanOption} onChange={onChosenOption} />*/}
                      {/*</Grid>*/}
                      <Grid item xs={12}>
                        <DropZone
                          options={{ multiple: false, noClick: true }}
                          onChange={handleChangeFiles}
                          rules={{
                            maxFile: 1,
                            minSize: 1,
                            maxSize: convertMbToByte(10),
                            acceptMimeTypes: SCAN_ACCEPT_MIME_TYPES,
                          }}
                          isShowErr={false}
                        >
                          <Typography variant="body14Light" whiteSpace="pre-wrap" textAlign="center">
                            アップロードするファイルをドラッグ&ドロップ
                            <br />
                            <Typography variant="body14Light" color={black[90]}>
                              （ファイル形式：{SCAN_ACCEPT_EXT_LABEL}に対応）
                            </Typography>
                            <br />
                            <Typography variant="body14Light" color={black[90]}>
                              ※1ファイルにつき求人票1件のみ対応しています。複数ある場合は分割してアップロードしてください。
                            </Typography>
                          </Typography>

                          <Typography variant="body14Light" textAlign="center">
                            または
                          </Typography>
                          <Button type="button" variant="outline">
                            ファイルを選択する
                          </Button>

                          <Typography variant="body14Light" color={orange[80]} textAlign="center">
                            PDF以外のファイルはプレビューに対応していません。
                          </Typography>
                        </DropZone>
                      </Grid>
                    </Grid>
                  </Stack>
                </Box>
              )}

              <Box>
                <hr color={divider.high} />
                <Stack direction="row" gap="16px" justifyContent="space-between" p="16px 32px" minHeight={69}>
                  <Button size="sm" variant="ghost" onClick={onClose}>
                    キャンセル
                  </Button>
                  {isSubmit ? (
                    <Button size="sm" disabled>
                      ドキュメントを処理中...
                    </Button>
                  ) : (
                    <Button onClick={onSubmit} size="sm" variant="primary" type="submit" startIcon={<IconSaveScanImage />}>
                      書類を読み込み
                    </Button>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </ModalContainer>
      </Modal>
      <ConfirmLeaveScanModal modal={confirmLeaveModal} onOk={confirmClose} />
    </>
  );
};

const ModalContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isResultData",
})<{ isResultData: boolean }>`
  width: ${({ isResultData }) => (isResultData ? "80vw" : "min(920px, 95vw)")};
  height: ${({ isResultData }) => (isResultData ? "min(789px, 95vh)" : "auto")};
  max-width: 80vw;
  min-width: min(1264px, 95vw);
  max-height: 95vh;
  background-color: ${background.white};
  box-shadow: 0px 8px 24px 0px #00000026;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  outline: none;
  &:focus {
    outline: none;
  }
`;

const ProcessingProgressBar = styled(Progress)`
  &.MuiLinearProgress-root {
    height: 8px;
    border-radius: ${rounded.xs};
    background-color: ${background.disable};
    .MuiLinearProgress-bar {
      background-color: ${button.primary};
      transition: transform 0.5s ease-out;
    }
  }
`;
